import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Users,
  Heart,
  Dumbbell,
  Laptop,
  Baby,
  MapPin,
  Shield,
  Check,
  TrendingUp,
} from "lucide-react";
import { HERO, ABOUT } from "@/lib/content";
import { api } from "@/lib/api";
import SubscribeForm from "@/components/SubscribeForm";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import SEO, { websiteJsonLd, localBusinessJsonLd } from "@/components/SEO";

/* ---------- icon helper ---------- */
const ICONS = { Sparkles, Users, Heart, Dumbbell, Laptop, Baby, MapPin, Shield, Check, TrendingUp };
function Icon({ name, size = 22, className = "text-[#E8A6B8]" }) {
  const C = ICONS[name] || Heart;
  return <C size={size} strokeWidth={1.5} className={className} />;
}

const WHO_I_HELP = [
  {
    title: "Women new to strength training",
    body: "Learn the fundamentals — squat, hinge, push, pull — with structure and support.",
    icon: "Sparkles",
    to: "/services#beginner-gym",
  },
  {
    title: "Mums rebuilding strength after children",
    body: "Postpartum strength coaching that's kind, structured and works around real life.",
    icon: "Baby",
    to: "/services#postpartum",
  },
  {
    title: "Women wanting gym confidence",
    body: "Walk into the weights area knowing exactly what to do — no more guesswork.",
    icon: "TrendingUp",
    to: "/services#beginner-gym",
  },
  {
    title: "Women wanting online coaching support",
    body: "Online coaching for women with weekly check-ins, accountability and personalised plans.",
    icon: "Laptop",
    to: "/services#online-strength",
  },
];

const WHAT_I_OFFER = [
  {
    title: "Online Strength Coaching",
    body: "Personalised online coaching for women — train at your own gym with weekly check-ins.",
    to: "/services",
    icon: "Laptop",
  },
  {
    title: "Postpartum Strength Coaching",
    body: "Rebuild strength after pregnancy with structured, beginner-friendly progressions.",
    to: "/services",
    icon: "Baby",
  },
  {
    title: "Beginner Gym Coaching",
    body: "Beginner gym coaching for women — build confidence and capability from day one.",
    to: "/services",
    icon: "Dumbbell",
  },
  {
    title: "Fitness Programs",
    body: "Self-paced strength programs designed to help women train smart and stay consistent.",
    to: "/programs",
    icon: "Sparkles",
  },
];

const WHY_PILLARS = [
  { label: "Women only", icon: "Users" },
  { label: "Beginner friendly", icon: "Sparkles" },
  { label: "London based", icon: "MapPin" },
  { label: "Online & in-person options", icon: "Laptop" },
  { label: "Strength and confidence focused", icon: "Heart" },
];

const FEATURED_SLUGS = [
  "why-women-should-lift-heavy",
  "how-to-rebuild-strength-after-having-children",
  "how-to-build-confidence-in-the-gym",
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#F6EFE9]">
      <SEO
        title="HerFitHub | Women's Strength Coaching with Maya"
        description="Women's strength coach in London helping women build strength, confidence and consistency. Online coaching for women, postpartum strength coaching and beginner gym coaching."
        path="/"
        keywords="women's strength coach London, women's personal trainer London, online coaching for women, beginner gym coaching for women, postpartum strength coaching, strength training for women"
        jsonLd={[websiteJsonLd, localBusinessJsonLd]}
      />
      <Hero />
      <WhoIHelp />
      <WhatIOffer />
      <WhyTrain />
      <FeaturedBlog />
      <Newsletter />
      <Testimonials />
      <FinalCTA />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 fade-in">
            <p className="eyebrow mb-6">Women's Strength Coaching · London</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#3A3A3A]">
              Women's Strength Coaching{" "}
              <span className="italic text-[#E8A6B8]">Designed Around Real Life</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-lg">
              Helping women build strength, confidence and trust in their bodies
              through beginner-friendly, supportive coaching.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs uppercase tracking-[0.16em] text-[#5C5C5C]">
              <span className="flex items-center gap-2"><Check size={14} className="text-[#E8A6B8]" /> Women only</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#E8A6B8]" /> Beginner friendly</span>
              <span className="flex items-center gap-2"><Check size={14} className="text-[#E8A6B8]" /> Online & in-person</span>
            </div>

            {/* Limited monthly intake — soft premium trust pill */}
            <div className="mt-6 fade-in" style={{ animationDelay: "200ms" }}>
              <span
                className="inline-flex items-center gap-2.5 bg-white/85 backdrop-blur-sm border border-[#F3C9D3]/70 rounded-full pl-3 pr-5 py-2 shadow-[0_8px_22px_rgba(232,166,184,0.16)]"
                data-testid="home-intake-pill"
                aria-label="Currently accepting a limited number of coaching clients each month"
              >
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#E8A6B8] opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E8A6B8]" />
                </span>
                <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
                  Currently Accepting
                </span>
                <span className="text-[#E8A6B8]" aria-hidden>·</span>
                <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                  Limited Monthly Intake
                </span>
              </span>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/booking" className="btn-pink" data-testid="hero-book-btn">
                Book Consultation
              </Link>
              <Link to="/services" className="btn-outline" data-testid="hero-services-btn">
                View Coaching Options
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] lg:h-[640px] bg-[#F3C9D3]">
              <img
                src={HERO.image}
                alt="Women's strength coach London — beginner gym coaching for women"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:bottom-6 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4 max-w-xs">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8]">Real life · Real strength</p>
                <p className="text-sm text-[#3A3A3A] mt-1 leading-snug">
                  Coaching for women who want to feel strong, confident and at home in their bodies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Decorative blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl" />
    </section>
  );
}

/* ---------- WHO I HELP ---------- */
function WhoIHelp() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Who I Help</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Real women, <span className="italic text-[#E8A6B8]">real goals</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C]">
            Whether you're picking up your first dumbbell, returning after kids,
            or looking for structured online support — there's a path here for
            you.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {WHO_I_HELP.map((c, i) => (
            <Link
              key={c.title}
              to={c.to}
              className="group bg-[#F6EFE9] rounded-[1.75rem] p-7 border border-[#F3C9D3]/30 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(232,166,184,0.18)] flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8A6B8] focus-visible:ring-offset-2"
              data-testid={`who-card-${i}`}
              aria-label={`${c.title} — learn more`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-white mb-5 border border-[#F3C9D3]/40 self-start transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]">
                <Icon name={c.icon} size={22} />
              </div>
              <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                {c.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed flex-1">
                {c.body}
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition-colors">
                Learn More
                <ArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- WHAT I OFFER ---------- */
function WhatIOffer() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">What I Offer</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Coaching designed <span className="italic text-[#E8A6B8]">around you</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {WHAT_I_OFFER.map((o, i) => (
            <Link
              key={o.title}
              to={o.to}
              className="group bg-white rounded-[1.75rem] p-7 border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(232,166,184,0.22)] flex flex-col"
              data-testid={`offer-card-${i}`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 mb-5 self-start">
                <Icon name={o.icon} size={22} />
              </div>
              <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                {o.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed flex-1">{o.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition">
                Learn More <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- WHY TRAIN WITH MAYA ---------- */
function WhyTrain() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3]">
            <img
              src={ABOUT.image}
              alt="Maya — women's personal trainer London, postpartum strength coaching"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-7">
          <p className="eyebrow mb-4">Why Train With Maya</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
            Supportive coaching <br />
            <span className="italic text-[#E8A6B8]">that actually fits</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            Women-only coaching focused on strength, confidence, consistency and
            realistic progress. Whether you're looking for a women's personal
            trainer in London or strength training for women online — you'll be
            met where you are.
          </p>
          <ul className="mt-9 space-y-3 max-w-md">
            {WHY_PILLARS.map((p) => (
              <li
                key={p.label}
                className="flex items-center gap-4 bg-white/70 backdrop-blur-sm rounded-2xl px-5 py-4 border border-white/50"
              >
                <span className="flex-shrink-0 inline-flex p-2 rounded-xl bg-[#F3C9D3]/60">
                  <Icon name={p.icon} size={18} />
                </span>
                <span className="text-sm font-medium text-[#3A3A3A]">{p.label}</span>
              </li>
            ))}
          </ul>
          <Link to="/about" className="btn-outline mt-10" data-testid="why-meet-maya">
            Meet Maya <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- FEATURED BLOG ---------- */
function FeaturedBlog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api
      .get("/blog/posts")
      .then((r) => {
        const all = r.data || [];
        // Try to load specifically the 3 featured posts; else fall back to first 3
        const featured = FEATURED_SLUGS.map((s) =>
          all.find((p) => p.slug === s)
        ).filter(Boolean);
        setPosts(featured.length === 3 ? featured : all.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow mb-3">Latest From The Journal</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A] leading-tight">
              Strength, mindset <br className="hidden sm:block" />
              <span className="italic text-[#E8A6B8]">and real-life fitness</span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] inline-flex items-center gap-2 transition"
            data-testid="all-articles-link"
          >
            All articles <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="group hfh-card overflow-hidden block bg-[#F6EFE9]"
              data-testid={`featured-blog-${p.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F3C9D3]/20">
                <img
                  src={p.image_url}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-7">
                <p className="eyebrow text-[#E8A6B8] mb-3">{p.category}</p>
                <h3 className="font-serif-display text-2xl text-[#3A3A3A] leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition">
                  Read More <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- NEWSLETTER ---------- */
function Newsletter() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SubscribeForm
          source="home"
          title="Get my free Strong Foundations programme"
          body="Drop your email and I'll send you Strong Foundations — a complete self-paced beginner programme to help you build real strength, structure and confidence. Soft inbox, never spam."
        />
      </div>
    </section>
  );
}

/* ---------- TESTIMONIALS ---------- */
function Testimonials() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <TestimonialsCarousel />
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative text-center"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-10 py-20 sm:py-24">
          <p className="eyebrow text-[#3A3A3A]/80 mb-4" data-testid="final-cta-eyebrow">
            Ready When You Are
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight max-w-2xl mx-auto">
            Ready To Feel <span className="italic">Strong Again?</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl mx-auto">
            Start your strength journey with coaching that supports your
            lifestyle, goals and confidence.
          </p>
          <Link
            to="/booking"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-10 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
            data-testid="final-cta-book"
          >
            Book Your Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
